import React, { ReactNode } from 'react';
import { Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router-dom';
import { LinkProps } from '@mui/material/Link';
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import colors from './themes-vars.module.scss';
import './shared-fonts.css';

const color = colors;

export type SemanticStatusPalette = {
  background: string;
  color: string;
};

export type SemanticStatusVariant = 'success' | 'warning' | 'info' | 'error' | 'neutral';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    body1Light: TypoCSS;
    body1Regular: TypoCSS;
    body2Medium: TypoCSS;
    bodyMedium11: TypoCSS;
    bodyMedium13: TypoCSS;
    body2SemiBold: TypoCSS;
    button: TypoCSS;
    button2: TypoCSS;
    caption: TypoCSS;
    captionSmall: TypoCSS;
    mini: TypoCSS;
    heading1: TypoCSS;
    heading2: TypoCSS;
    heading3: TypoCSS;
    heading4: TypoCSS;
    heading5: TypoCSS;
    heading6: TypoCSS;
    heading7: TypoCSS;
    heading8: TypoCSS;
    subtitle1: TypoCSS;
    subtitle2Medium: TypoCSS;
  }

  interface Palette {
    neutral: {
      white: string;
      10: string;
      15: string;
      20: string;
      30: string;
      40: string;
      50: string;
      60: string;
      70: string;
      80: string;
      90: string;
      black: string;
    };
    red: {
      20: string;
      40: string;
      60: string;
      80: string;
      100: string;
    };
    purple: {
      20: string;
      40: string;
      60: string;
      80: string;
      100: string;
    };
    beige: {
      20: string;
      40: string;
      60: string;
      80: string;
      100: string;
    };
    green: {
      20: string;
      40: string;
      60: string;
      80: string;
      100: string;
    };
    blue: {
      20: string;
      40: string;
      60: string;
      80: string;
      100: string;
    };
    other: {
      yellow: string;
      yellowStar: string;
      yellowMedium: string;
    };
    gradient: {
      red: string;
      blue: string;
      green: string;
      buttonGradient: string;
    };
    semanticStatus: {
      success: SemanticStatusPalette;
      warning: SemanticStatusPalette;
      info: SemanticStatusPalette;
      error: SemanticStatusPalette;
      neutral: SemanticStatusPalette;
    };
  }

  interface PaletteOptions {
    neutral: {
      white: string;
      10: string;
      15: string;
      20: string;
      30: string;
      40: string;
      50: string;
      60: string;
      70: string;
      80: string;
      90: string;
      black: string;
    };
    red: {
      20: string;
      40: string;
      60: string;
      80: string;
      100: string;
    };
    purple: {
      20: string;
      40: string;
      60: string;
      80: string;
      100: string;
    };
    beige: {
      20: string;
      40: string;
      60: string;
      80: string;
      100: string;
    };
    green: {
      20: string;
      40: string;
      60: string;
      80: string;
      100: string;
    };
    blue: {
      20: string;
      40: string;
      60: string;
      80: string;
      100: string;
    };
    gradient: {
      red: string;
      blue: string;
      green: string;
      buttonGradient: string;
    };
    other: {
      yellow: string;
      yellowStar: string;
      yellowMedium: string;
    };
    semanticStatus: {
      success: SemanticStatusPalette;
      warning: SemanticStatusPalette;
      info: SemanticStatusPalette;
      error: SemanticStatusPalette;
      neutral: SemanticStatusPalette;
    };
  }

  interface TypoCSS {
    fontWeight: number;
    fontFamily: string;
    fontSize: number;
    lineHeight: string;
    display?: string;
    textTransform?: string;
    [key: string]: any;
  }

  interface TypographyPropsVariantOverrides {
    body1Light: TypoCSS;
    body1Regular: TypoCSS;
    body2Medium: TypoCSS;
    bodyMedium11: TypoCSS;
    bodyMedium13: TypoCSS;
    body2SemiBold: TypoCSS;
    button: TypoCSS;
    button2: TypoCSS;
    caption: TypoCSS;
    captionSmall: TypoCSS;
    mini: TypoCSS;
    heading1: TypoCSS;
    heading2: TypoCSS;
    heading3: TypoCSS;
    heading4: TypoCSS;
    heading5: TypoCSS;
    heading6: TypoCSS;
    heading7: TypoCSS;
    heading8: TypoCSS;
    subtitle1: TypoCSS;
    subtitle2Medium: TypoCSS;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    body1Light: TypoCSS;
    body1Regular: TypoCSS;
    body2Medium: TypoCSS;
    bodyMedium11: TypoCSS;
    bodyMedium13: TypoCSS;
    body2SemiBold: TypoCSS;
    button: TypoCSS;
    button2: TypoCSS;
    caption: TypoCSS;
    captionSmall: TypoCSS;
    mini: TypoCSS;
    heading1: TypoCSS;
    heading2: TypoCSS;
    heading3: TypoCSS;
    heading4: TypoCSS;
    heading5: TypoCSS;
    heading5Small: TypoCSS;
    heading6: TypoCSS;
    heading7: TypoCSS;
    heading8: TypoCSS;
    subtitle1: TypoCSS;
    subtitle2Medium: TypoCSS;
    subtitle2Small: TypoCSS;
  }
}

declare module '@mui/material/Typography' {
  export interface TypographyPropsVariantOverrides {
    body1Light: true;
    body1Regular: true;
    body2Medium: true;
    bodyMedium11: true;
    bodyMedium13: true;
    body2SemiBold: true;
    button: true;
    button2: true;
    caption: true;
    captionSmall: true;
    mini: true;
    heading1: true;
    heading2: true;
    heading3: true;
    heading4: true;
    heading5: true;
    heading6: true;
    heading7: true;
    heading8: true;
    subtitle1: true;
    subtitle2Medium: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    button: true;
    button2: true;
    caption: true;
    captionSmall: true;
    mini: true;
  }
}

const LinkBehavior = React.forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }>(
  (props, ref) => {
    const { href, ...other } = props;
    // Map href (Material UI) -> to (react-router)
    return <RouterLink ref={ref} to={href} {...other} />;
  },
);

export const theme = createTheme({
  palette: {
    primary: {
      main: '#ff6b97',
    },
    secondary: {
      main: '#c2c5f9',
    },
    neutral: {
      white: color.white,
      10: color.neutral10,
      15: color.neutral15,
      20: color.neutral20,
      30: color.neutral30,
      40: color.neutral40,
      50: color.neutral50,
      60: color.neutral60,
      70: color.neutral70,
      80: color.neutral80,
      90: color.neutral90,
      black: color.black,
    },
    red: {
      20: color.red20,
      40: color.red40,
      60: color.red60,
      80: color.red80,
      100: color.red100,
    },
    purple: {
      20: color.purple20,
      40: color.purple40,
      60: color.purple60,
      80: color.purple80,
      100: color.purple100,
    },
    beige: {
      20: color.beige20,
      40: color.beige40,
      60: color.beige60,
      80: color.beige80,
      100: color.beige100,
    },
    green: {
      20: color.green20,
      40: color.green40,
      60: color.green60,
      80: color.green80,
      100: color.green100,
    },
    blue: {
      20: color.blue20,
      40: color.blue40,
      60: color.blue60,
      80: color.blue80,
      100: color.blue100,
    },
    gradient: {
      red: color.gradientRed,
      blue: color.gradientBlue,
      green: color.gradientGreen,
      buttonGradient: color.buttonGradient,
    },
    other: {
      yellow: color.yellow,
      yellowStar: color.yellowStar,
      yellowMedium: color.yellowMedium,
    },
    semanticStatus: {
      success: {
        background: color.semanticStatusSuccessBackground,
        color: color.semanticStatusSuccessColor,
      },
      warning: {
        background: color.semanticStatusWarningBackground,
        color: color.semanticStatusWarningColor,
      },
      info: {
        background: color.semanticStatusInfoBackground,
        color: color.semanticStatusInfoColor,
      },
      error: {
        background: color.semanticStatusErrorBackground,
        color: color.semanticStatusErrorColor,
      },
      neutral: {
        background: color.semanticStatusNeutralBackground,
        color: color.semanticStatusNeutralColor,
      },
    },
  },
  components: {
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      } as LinkProps,
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          width: '210px',
        },
        list: {
          padding: '0px',
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'button' },
          style: {
            background: color.buttonGradient,
            color: color.black,
            '&:hover': {
              background: color.black,
              color: color.white,
            },
            '&:disabled': {
              background: color.neutral20,
              color: color.neutral30,
            },
          },
        },
        {
          props: { variant: 'button2' },
          style: {
            background: color.black,
            color: color.white,
            '&:hover': {
              backgroundColor: color.neutral90,
              color: color.white,
            },
            '&:disabled': {
              background: color.neutral10,
              color: color.neutral30,
            },
          },
        },
        {
          props: { variant: 'caption' },
          style: {
            fontWeight: 400,
            fontFamily: '"Poppins Regular", sans-serif',
            fontSize: 14,
            lineHeight: '19.6px',
            display: 'block',
          },
        },
      ],
    },
    MuiAlert: {
      styleOverrides: {
        standard: {
          backgroundColor: 'white',
        },
        outlined: {
          border: `1px solid black`,
        },
      },
    },
  },
  typography: {
    fontFamily: ['Poppins Regular', 'Open Sans'].join(','),
    body1Light: {
      fontWeight: 300,
      fontFamily: '"Poppins Light", sans-serif',
      fontSize: 14,
      display: 'block',
      lineHeight: '19.6px',
    },
    body1Regular: {
      fontWeight: 400,
      fontFamily: '"Poppins Regular", sans-serif',
      fontSize: 14,
      lineHeight: '19.6px',
      display: 'block',
    },
    body2Medium: {
      fontWeight: 500,
      display: 'block',
      fontFamily: '"Poppins Medium", sans-serif',
      fontSize: 14,
      lineHeight: '19.6px',
    },
    bodyMedium13: {
      fontWeight: 500,
      display: 'block',
      fontFamily: '"Poppins Medium", sans-serif',
      fontSize: 12,
      lineHeight: '19.6px',
    },
    bodyMedium11: {
      fontWeight: 500,
      display: 'block',
      fontFamily: '"Poppins Medium", sans-serif',
      fontSize: 11,
      lineHeight: '10px',
    },
    body2SemiBold: {
      fontWeight: 600,
      display: 'block',
      fontFamily: '"Poppins SemiBold", sans-serif',
      fontSize: 14,
      lineHeight: '19.6px',
    },
    button: {
      fontWeight: 600,
      fontFamily: '"Poppins SemiBold", sans-serif',
      fontSize: 14,
      lineHeight: '19.6px',
    },
    button2: {
      fontWeight: 700,
      fontFamily: '"Poppins Bold", sans-serif',
      fontSize: 14,
      lineHeight: '19.6px',
    },
    caption: {
      fontWeight: 400,
      fontFamily: '"Poppins Regular", sans-serif',
      fontSize: 12,
      lineHeight: '16.8px',
    },
    captionSmall: {
      fontWeight: 300,
      fontFamily: '"Poppins Regular", sans-serif',
      fontSize: 10,
      lineHeight: '5px',
    },
    mini: {
      fontWeight: 200,
      fontFamily: '"Poppins Regular", sans-serif',
      fontSize: 8,
      lineHeight: '9.8px',
    },
    heading1: {
      fontWeight: 400,
      fontFamily: '"Instrument Serif Regular"',
      fontSize: 80,
      lineHeight: '100%',
      display: 'block',
      textTransform: 'uppercase',
      '@media (max-width: 600px)': {
        fontSize: 40,
        lineHeight: '120%',
      },
    },
    heading2: {
      fontWeight: 400,
      fontFamily: '"Instrument Serif Regular"',
      fontSize: 48,
      lineHeight: '100%',
      display: 'block',
      textTransform: 'uppercase',
      '@media (max-width: 600px)': {
        fontSize: 32,
        lineHeight: '120%',
      },
    },
    heading3: {
      fontWeight: 400,
      fontFamily: '"Instrument Serif Regular"',
      fontSize: 40,
      lineHeight: '100%',
      display: 'block',
      textTransform: 'uppercase',
      '@media (max-width: 600px)': {
        fontSize: 28,
        lineHeight: '120%',
      },
    },
    heading4: {
      fontWeight: 400,
      fontFamily: '"Instrument Serif Regular"',
      fontSize: 36,
      lineHeight: '100%',
      display: 'block',
      textTransform: 'uppercase',
      '@media (max-width: 600px)': {
        fontSize: 24,
        lineHeight: '120%',
      },
    },
    heading5: {
      fontWeight: 400,
      fontFamily: '"Instrument Serif Regular"',
      fontSize: 32,
      lineHeight: '100%',
      display: 'block',
      textTransform: 'uppercase',
      '@media (max-width: 600px)': {
        fontSize: 22,
        lineHeight: '120%',
      },
    },
    heading5Small: {
      fontWeight: 400,
      fontFamily: '"Instrument Serif Regular"',
      fontSize: 27,
      lineHeight: '1.05',
      display: 'block',
    },
    heading6: {
      fontWeight: 400,
      fontFamily: '"Instrument Serif Regular"',
      fontSize: 24,
      lineHeight: '100%',
      display: 'block',
      textTransform: 'uppercase',
      '@media (max-width: 600px)': {
        fontSize: 20,
        lineHeight: '120%',
      },
    },
    heading7: {
      fontWeight: 400,
      fontFamily: '"Instrument Serif Regular"',
      fontSize: 22,
      lineHeight: '100%',
      display: 'block',
      textTransform: 'uppercase',
      '@media (max-width: 600px)': {
        fontSize: 18,
        lineHeight: '120%',
      },
    },
    heading8: {
      fontWeight: 400,
      fontFamily: '"Instrument Serif Regular"',
      fontSize: 20,
      lineHeight: '100%',
      display: 'block',
      textTransform: 'uppercase',
    },
    subtitle1: {
      fontWeight: 400,
      fontFamily: '"Poppins Regular", sans-serif',
      fontSize: 16,
      lineHeight: '22.4px',
    },
    subtitle2Medium: {
      fontWeight: 500,
      fontFamily: '"Poppins Medium", sans-serif',
      fontSize: 16,
      lineHeight: '22.4px',
    },
    subtitle2Small: {
      fontWeight: 500,
      fontFamily: '"Poppins Medium", sans-serif',
      fontSize: 13,
      lineHeight: '1.55px',
    },
  },
});

type ThemingProps = {
  children: ReactNode;
};

const Theming = ({ children }: ThemingProps) => {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </StyledEngineProvider>
  );
};

export default Theming;
export { Theming };
